import React from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../utils/dateUtils';
import { formatPartyDisplay } from '../../utils/partyUtils';
import { DETAIL_LINK, DetailCard, MUTED_TEXT } from '../ballot-initiatives/detail/detailAtoms';
import { PartyDot, StatusPill } from './PersonAtoms';
import { electTitle, outcomeLabel, takesOfficeText } from './personDisplay';
import type { OfficeElectData } from './types';

const ROW = 'flex flex-wrap items-baseline gap-x-2 text-sm';
const LABEL = `w-28 shrink-0 ${MUTED_TEXT}`;

/**
 * What a winner won, between results day and taking office
 * (Documentation/ELECTION_OUTCOMES_PRD.md §5.13): the office, when they take it,
 * the result, and who they succeed. No vote counts — this records who won.
 */
const OfficeElectCard: React.FC<{ officeElect: OfficeElectData; isPublic?: boolean }> = ({ officeElect, isPublic = false }) => {
  const personHref = (personId: string) => (isPublic ? `/public/candidates/${personId}` : `/candidates/${personId}`);
  const party = formatPartyDisplay(officeElect.party ?? '') || officeElect.party;
  const unofficial = officeElect.result_status === 'unofficial';

  return (
    <DetailCard
      id="person-office-elect"
      title={electTitle(officeElect.title)}
      subLine={[officeElect.title, officeElect.district_label, party].filter(Boolean).join(' · ')}
      aside={unofficial ? <span className={MUTED_TEXT}>Unofficial results</span> : undefined}
    >
      <div className="space-y-2.5">
        <div className={ROW}>
          <span className={LABEL}>Takes office</span>
          <span className="font-medium text-text-primary dark:text-text-dark-primary">{takesOfficeText(officeElect)}</span>
        </div>
        {officeElect.term_start && officeElect.term_end && (
          <div className={ROW}>
            <span className={LABEL}>Term</span>
            <span className="text-text-primary dark:text-text-dark-primary">
              {formatDate(officeElect.term_start)} – {formatDate(officeElect.term_end)}
              {officeElect.cycle_label ? <span className={MUTED_TEXT}> ({officeElect.cycle_label})</span> : null}
            </span>
          </div>
        )}
        <div className={ROW}>
          <span className={LABEL}>Succeeds</span>
          <span className="text-text-primary dark:text-text-dark-primary">
            {officeElect.re_elected ? 'Re-elected to a new term' : officeElect.succeeds ? (
              <Link to={personHref(officeElect.succeeds.person_id)} className={DETAIL_LINK}>{officeElect.succeeds.name}</Link>
            ) : '—'}
          </span>
        </div>
        <div className={ROW}>
          <span className={LABEL}>Result</span>
          <span className="text-text-primary dark:text-text-dark-primary">
            Won the {officeElect.election_year ?? ''} general election{unofficial ? ' (unofficial results)' : ''}
          </span>
        </div>
      </div>

      {officeElect.contest_candidates.length > 0 && (
        <ul className="mt-4 space-y-2">
          {officeElect.contest_candidates.map((c) => (
            <li
              key={c.person_id}
              className="flex items-center justify-between gap-3 rounded-[10px] border border-border-light bg-background-secondary px-3.5 py-2.5 dark:border-border-dark dark:bg-background-dark-tertiary"
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <PartyDot party={c.party ?? ''} />
                <Link to={personHref(c.person_id)} className={`text-sm font-semibold ${DETAIL_LINK}`}>{c.name}</Link>
                <span className={`text-[13px] ${MUTED_TEXT}`}>{formatPartyDisplay(c.party ?? '') || c.party}</span>
              </span>
              <StatusPill status={outcomeLabel(c.status)} />
            </li>
          ))}
        </ul>
      )}
    </DetailCard>
  );
};

export default OfficeElectCard;
